import React, { useState } from "react";
import { X, Copy, Check, KeyRound } from "lucide-react";

/**
 * DEV-013A · Show a freshly-minted temporary password ONCE after an
 * administrator hits "Reset password" for a target user. Copies to the
 * clipboard in one click. The dialog explicitly states the password
 * will not be shown again and that the user MUST change it on next
 * sign-in.
 *
 * The parent is responsible for opening this modal only after the
 * backend has responded with a `temporary_password` field.
 */
export default function TempPasswordModal({
    open, onClose, user, temporaryPassword,
}) {
    const [copied, setCopied] = useState(false);
    if (!open) return null;

    async function copy() {
        try {
            await navigator.clipboard.writeText(temporaryPassword);
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        } catch {
            // ignore - user can still select the field manually
        }
    }

    return (
        <div
            className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4"
            data-testid="temp-password-modal"
            role="dialog"
            aria-modal="true"
        >
            <div className="relative w-full max-w-md rounded-xl bg-white shadow-xl border border-[#E5E7EB]">
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close"
                    data-testid="temp-password-close"
                    className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-md text-[#4B5565] hover:text-[#131B2F] hover:bg-[#F5F5F7]"
                >
                    <X size={16} />
                </button>
                <div className="px-6 pt-6 pb-4 border-b border-[#F0F1F5]">
                    <div className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-full bg-[#E6F1DE] flex items-center justify-center text-[#2E7E22]">
                            <KeyRound size={16} strokeWidth={2} />
                        </div>
                        <div>
                            <div className="text-sm font-semibold text-[#131B2F]">
                                Temporary password ready
                            </div>
                            <div className="text-xs text-[#4B5565]">
                                for {user?.name || user?.email}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="px-6 py-5 space-y-4">
                    <p className="text-sm text-[#131B2F]">
                        Share this password with the user through a trusted channel.
                        They will be forced to set a new password when they next sign in.
                    </p>

                    <div className="relative">
                        <input
                            readOnly
                            value={temporaryPassword || ""}
                            data-testid="temp-password-value"
                            onFocus={(e) => e.target.select()}
                            className="w-full h-11 rounded-md border border-[#E5E7EB] bg-[#F5F5F7] px-3.5 pr-24 text-base font-mono tracking-wide outline-none focus:border-[#3E9E2E]"
                        />
                        <button
                            type="button"
                            onClick={copy}
                            data-testid="temp-password-copy"
                            className="absolute right-1.5 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 h-8 px-3 rounded-md bg-[#131B2F] text-white text-xs font-medium hover:bg-[#0B1220]"
                        >
                            {copied ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy</>}
                        </button>
                    </div>

                    <div className="rounded-md bg-[#FEF3C7] border border-[#F59E0B]/40 px-3 py-2.5 text-xs text-[#7C4A03]"
                         data-testid="temp-password-warning">
                        This password will not be shown again. If you close this dialog
                        without copying it, run the reset a second time to mint a new one.
                    </div>

                    <ul className="text-xs text-[#4B5565] space-y-1.5 list-disc list-inside">
                        <li>All active sessions for this account have been revoked.</li>
                        <li>The user must change this password on their next sign-in.</li>
                        <li>Existing projects, tasks, appointments and documents are unchanged.</li>
                    </ul>
                </div>

                <div className="px-6 py-4 border-t border-[#F0F1F5] flex justify-end">
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="temp-password-done"
                        className="h-10 px-4 rounded-md bg-[#3E9E2E] hover:bg-[#2E7E22] text-white text-sm font-medium"
                    >
                        I've copied it, close
                    </button>
                </div>
            </div>
        </div>
    );
}
